import React, {
  useRef,
  createRef,
  useEffect,
  useCallback,
  useState,
  memo,
} from 'react'
import PropTypes from 'prop-types'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { useAnimation } from 'framer-motion'
import {
  siteData as siteDataState,
  teamData as teamDataState,
} from '../../store/dataStore'
import useWindowResize from '../../hooks/useWindowResize'
import {
  currentSection as currentSectionState,
  showMember as showMemberState,
  selectedMember as selectedMemberState,
} from '../../store/uiStore'
import CustomScroller from 'react-custom-scroller'

import {
  TeamSection,
  TeamWrapper,
  TeamTitle,
  TeamDescription,
  TeamMembers,
  TeamMember,
} from '../../styles/teamStyles'

const Member = memo(
  React.forwardRef(({ index, member }, ref) => {
    const windowSize = useWindowResize()

    const setSelectedMember = useSetRecoilState(selectedMemberState)
    const setCurrentSection = useSetRecoilState(currentSectionState)
    const setShowMember = useSetRecoilState(showMemberState)

    const handleMemberClick = useCallback(() => {
      setSelectedMember(index)
      setShowMember(true)
      setCurrentSection('miembro')
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [])

    return (
      <TeamMember ref={ref}>
        <button
          onClick={() => handleMemberClick()}
          aria-label='Ver detalles'
          title='Ver detalles'>
          <img src={member.image} alt={member.name} />
        </button>
        {windowSize.width > 420 && <h3>{member.name}</h3>}
      </TeamMember>
    )
  })
)

const Team = ({ visible }) => {
  const teamData = useRecoilValue(teamDataState)
  const members = teamData.members
  const siteData = useRecoilValue(siteDataState)

  const teamSection = useRef(null)
  const teamTitle = useRef(null)
  const teamDesc = useRef(null)
  const teamGroup = useRef(null)
  const teamMembers = useRef(members.map(() => createRef()))
  const teamScroller = useRef(null)

  const teamSectionVars = {
    hidden: {
      visibility: 'hidden',
      zIndex: -1,
      opacity: 0,
    },
    visible: {
      visibility: 'visible',
      zIndex: 1,
      opacity: 1,
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 1,
        delay: 0,
      },
      transitionEnd: {
        visibility: 'hidden',
        zIndex: -1,
      },
    },
  }

  const teamTitleVars = {
    hidden: {
      opacity: 0,
      y: '-270%',
      transition: {
        duration: 1.5,
        ease: 'easeOut',
        delay: 0,
      },
    },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  const teamDescVars = {
    hidden: {
      opacity: 0,
      y: '-85%',
      transition: {
        duration: 1,
        ease: 'easeOut',
        delay: 0,
      },
    },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  const teamGroupVars = {
    hidden: {
      opacity: 0,
      y: '50%',
      transition: {
        duration: 1,
        ease: 'easeOut',
        delay: 0,
      },
    },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  const teamAnims = useAnimation(teamSectionVars)
  const teamTitleAnims = useAnimation(teamTitleVars)
  const teamDescAnims = useAnimation(teamDescVars)
  const teamGroupAnims = useAnimation(teamGroupVars)

  const [readMore, setReadMore] = useState(false)
  const [label, setLabel] = useState('')

  const handleReadMore = () => {
    setReadMore(!readMore)
  }

  useEffect(() => {
    if (visible) {
      teamAnims.start('visible')
      teamTitleAnims.start('visible')
      teamDescAnims.start('visible')
      teamGroupAnims.start('visible')
      teamScroller.current.scrollTop = 0
    } else {
      teamTitleAnims.start('hidden')
      teamDescAnims.start('hidden')
      teamGroupAnims.start('hidden')
      teamAnims.start('exit')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  useEffect(() => {
    if (readMore) {
      const collapse = siteData.language === 'es' ? 'Cerrar' : 'Close'
      setLabel(collapse)
    } else {
      const more = siteData.language === 'es' ? 'Leer más' : 'Read more'
      setLabel(more)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readMore])

  return (
    <TeamSection
      ref={teamSection}
      initial='hidden'
      animate={teamAnims}
      variants={teamSectionVars}
      transition={{
        opacity: {
          duration: 1.5,
          ease: 'linear',
        },
        default: {
          duration: 0,
          ease: 'none',
        },
      }}>
      <CustomScroller
        ref={teamScroller}
        className='Scroller'
        innerClassName='ScrollerContent'>
        <TeamWrapper>
          <TeamTitle
            ref={teamTitle}
            initial='hidden'
            animate={teamTitleAnims}
            variants={teamTitleVars}
            transition={{
              duration: 1.25,
              ease: 'easeOut',
              delay: 0,
            }}>
            {teamData.title}
          </TeamTitle>
          <TeamDescription
            ref={teamDesc}
            initial='hidden'
            className={readMore ? 'showMore' : ''}
            animate={teamDescAnims}
            variants={teamDescVars}
            transition={{
              duration: 1,
              ease: 'easeOut',
              delay: 0.5,
            }}>
            <div dangerouslySetInnerHTML={{ __html: teamData.description }} />
            <button className='readMoreMobile' onClick={() => handleReadMore()}>
              {label}
            </button>
          </TeamDescription>
          <TeamMembers
            ref={teamGroup}
            initial='hidden'
            animate={teamGroupAnims}
            variants={teamGroupVars}
            transition={{
              duration: 1,
              ease: 'easeOut',
              delay: 1,
            }}>
            <div className='TeamWrapper'>
              {members.map((member, index) => (
                <Member
                  key={`team-${member.name}-${index}`}
                  ref={teamMembers.current[index]}
                  index={index}
                  member={member}
                />
              ))}
            </div>
          </TeamMembers>
        </TeamWrapper>
      </CustomScroller>
    </TeamSection>
  )
}

Team.propTypes = {
  visible: PropTypes.bool.isRequired,
}

export default memo(Team)
